/**
 * Box component without className prop
 */
import React from 'react';
import BaseBox from '../BaseBox';
import PropTypes from 'prop-types';

// eslint-disable-next-line react/prop-types
const Box = ({className, ...props}) => <BaseBox {...props} data-hb-id="Box" />;

Box.propTypes = {
  // The HTML element that the Box will render as.
  is: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element,
  ]),
  // The CSS display property of the Box
  display: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp320: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp480: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp640: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp800: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp960: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1120: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1280: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1440: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1600: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
    }),
    PropTypes.oneOf([
      'none',
      'flex',
      'block',
      'inline',
      'inline-block',
      'inline-flex',
    ]),
  ]),
  // The factor that the Box grows by when within a flex container
  flexGrow: PropTypes.oneOf([1, 0, 'auto']),
  // The factor that the Box shrinks by when within a flex container
  flexShrink: PropTypes.oneOf([1, 0, 'auto']),
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    // Will create `data-enzyme-id=""` attribute
    enzyme: PropTypes.string,
    // Will create `data-codeception-id=""` attribute
    codeception: PropTypes.string,
  }),
  // The margin applied (on all sides) to the Box component
  m: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The padding applied (on all sides) to the Box component
  p: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The bottom margin applied to the Box component
  mb: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The bottom padding applied to the Box component
  pb: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The top margin applied to the Box component
  mt: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The top padding applied to the Box component
  pt: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The right margin applied to the Box component
  mr: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The right padding applied to the Box component
  pr: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The left margin applied to the Box component
  ml: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The left padding applied to the Box component
  pl: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The horizontal (aka "x axis") margin applied to the Box component
  // This will apply margin-left and margin-right to the Box
  mx: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The vertical (aka "y axis") margin applied to the Box component
  // This will apply margin-top and margin-bottom to the Box
  my: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The horizontal (aka "x axis") padding applied to the Box component
  // This will apply padding-left and padding-right to the Box
  px: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The vertical (aka "y axis") padding applied to the Box component
  // This will apply padding-top and padding-bottom to the Box
  py: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The positioning applied to the box component
  // Note that this component does not provide any fallbacks for position sticky
  position: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp320: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp480: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp640: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp800: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp960: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1120: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1280: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1440: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1600: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
    }),
    PropTypes.oneOf(['absolute', 'relative', 'fixed', 'static', 'sticky']),
  ]),
  // The top offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The bottom offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The left offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The right offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The CSS Flex Direction property applied to the Box component
  flexDirection: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
      bp320: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp480: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp640: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp800: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp960: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1120: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1280: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1440: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1600: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
    }),
    PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
  ]),
  // The CSS justify content property applied to the Box component
  justifyContent: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp320: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp480: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp640: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp800: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp960: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1120: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1280: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1440: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1600: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
    }),
    PropTypes.oneOf([
      'flex-start',
      'center',
      'flex-end',
      'space-between',
      'space-evenly',
      'space-around',
    ]),
  ]),
  // The CSS Align Items property applied to the Box component
  alignItems: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp320: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp480: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp640: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp800: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp960: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1120: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1280: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1440: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1600: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
    }),
    PropTypes.oneOf([
      'center',
      'start',
      'end',
      'flex-start',
      'flex-end',
      'stretch',
      'baseline',
    ]),
  ]),
  // The CSS Flex Wrap property applied to the Box component
  // Only applies when it is a flex container
  flexWrap: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['wrap', 'nowrap']),
      bp320: PropTypes.oneOf(['wrap', 'nowrap']),
      bp480: PropTypes.oneOf(['wrap', 'nowrap']),
      bp640: PropTypes.oneOf(['wrap', 'nowrap']),
      bp800: PropTypes.oneOf(['wrap', 'nowrap']),
      bp960: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1120: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1280: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1440: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1600: PropTypes.oneOf(['wrap', 'nowrap']),
    }),
    PropTypes.oneOf(['wrap', 'nowrap']),
  ]),
  // The alignment of the Box element
  // This is a responsive prop that accepts either an object of breakpoint: alignment
  // or just an alignment value for all breakpoints
  textAlign: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['left', 'center', 'right']),
      bp320: PropTypes.oneOf(['left', 'center', 'right']),
      bp480: PropTypes.oneOf(['left', 'center', 'right']),
      bp640: PropTypes.oneOf(['left', 'center', 'right']),
      bp800: PropTypes.oneOf(['left', 'center', 'right']),
      bp960: PropTypes.oneOf(['left', 'center', 'right']),
      bp1120: PropTypes.oneOf(['left', 'center', 'right']),
      bp1280: PropTypes.oneOf(['left', 'center', 'right']),
      bp1440: PropTypes.oneOf(['left', 'center', 'right']),
      bp1600: PropTypes.oneOf(['left', 'center', 'right']),
    }),
    PropTypes.oneOf(['left', 'center', 'right']),
  ]),
  // The flex order of the Box component
  flexOrder: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),
  // The flex-basis of the Box
  // This is a responsive prop that accepts either an object of breakpoint: size
  // or just a flex-basis value for all breakpoints
  // This value will derive to flex-basis: calc( 100% / 12 * [gutter size])
  size: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp320: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp480: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp640: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp800: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp960: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1120: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1280: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1440: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1600: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    }),
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),
  // @ignore-prop-docs
  'data-hb-id': PropTypes.string,
  // Background color
  bgColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['white', 'black--10', 'neutral', 'primary', 'b2b']),
      bp320: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp480: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp640: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp800: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp960: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1120: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1280: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1440: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1600: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
    }),
    PropTypes.oneOf(['white', 'black--10', 'neutral', 'primary', 'b2b']),
  ]),
  // This will apply a top border to the Box
  borderTopColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a bottom border to the Box
  borderBottomColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a right border to the Box
  borderRightColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a left border to the Box
  borderLeftColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a border to all 4 sides of the Box
  borderColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // The number of lines that the text content will be truncated at
  truncatedLines: PropTypes.oneOf([null, 1, 2]),
  // The height applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The minHeight applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The maxHeight applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The width applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The minWidth applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The maxWidth applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The border radius of the box applied to the top left corner
  borderTopLeftRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to the top right corner
  borderTopRightRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to the bottom left corner
  borderBottomLeftRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to the bottom right corner
  borderBottomRightRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to all corners
  borderRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The horizontal (aka "x-axis") overflow applied to the Box
  overflowX: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp320: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp480: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp640: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp800: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp960: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1120: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1280: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1440: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1600: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
    }),
    PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
  ]),
  // The vertical (aka "y-axis") overflow applied to the Box
  overflowY: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp320: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp480: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp640: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp800: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp960: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1120: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1280: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1440: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1600: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
    }),
    PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
  ]),
  // The overflow (in either axis) applied to the Box
  overflow: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp320: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp480: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp640: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp800: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp960: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1120: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1280: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1440: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1600: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
    }),
    PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
  ]),
};

Box.defaultProps = {
  is: 'div',
  display: null,
  flexGrow: null,
  flexShrink: null,
  flexOrder: null,
  testIds: {},
  m: null,
  p: null,
  mb: null,
  pb: null,
  mt: null,
  pt: null,
  mr: null,
  pr: null,
  ml: null,
  pl: null,
  mx: null,
  my: null,
  px: null,
  py: null,
  flexDirection: null,
  justifyContent: null,
  alignItems: null,
  flexWrap: null,
  textAlign: null,
  position: null,
  top: null,
  left: null,
  right: null,
  bottom: null,
  size: null,
  'data-hb-id': null,
  bgColor: null,
  truncatedLines: null,
  borderTopColor: null,
  borderBottomColor: null,
  borderRightColor: null,
  borderLeftColor: null,
  borderColor: null,
  height: null,
  minHeight: null,
  maxHeight: null,
  width: null,
  minWidth: null,
  maxWidth: null,
  borderTopLeftRadius: null,
  borderTopRightRadius: null,
  borderBottomLeftRadius: null,
  borderBottomRightRadius: null,
  borderRadius: null,
  overflowX: null,
  overflowY: null,
  overflow: null,
};

Box.breakpoints = BaseBox.breakpoints;
Box.convertResponsivePropToClassnames =
  BaseBox.convertResponsivePropToClassnames;
export default Box;
